import type { Author, HeroVideo, Meta, VideoSeo } from '../../../generated/frontend'
import { generateSchemaFromSnippet } from '../../../shared/utils/get-schema-from-snippet'
import { getTimestampToIso } from '../../../shared/utils/timestamp-to-iso'

type SchemaParams = {
  meta: Meta
  authors?: Author[]
  imageList?: string[]
  video?: VideoData
  schemaType?: 'Article' | 'NewsArticle'
}

export type VideoData = {
  heroVideo: HeroVideo | null
  customSeo: VideoSeo[] | null
}

export const generateSchema = ({
  meta: { title, desc, canonical, publishedDateTime, updatedDateTime, reviewSchema },
  authors = [],
  imageList = [],
  video,
  schemaType = 'Article',
}: SchemaParams) => {
  const schemas: JSX.Element[] = []
  const baseSchema = (
    <script type="application/ld+json">{`
        {
          "@context": "http://schema.org",
          "@type": "${schemaType}",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "${canonical}"
          },
          "headline": "${title}",
          "description": "${desc}",
          ${imageList.length ? `"image": ${JSON.stringify(imageList.filter(Boolean), null, 2)},` : ''}
          ${
            authors?.length
              ? `
          "author": {
            "@type": "Person",
            "name": "${authors[0].name}",
            ${authors[0].about ? `"description": "${authors[0].about}",` : ''}
            ${authors[0].jobTitle ? `"jobTitle": "${authors[0].jobTitle}",` : ''}
            ${
              authors[0].image
                ? `
            "image": {
              "@type": "ImageObject",
              "url": "${authors[0].image?.src}",
              "caption": "${authors[0].name}"
            },`
                : ''
            }
            "url": "https://www.which.co.uk${
              authors[0].slug === null
                ? '/about-which/who-we-are'
                : `/news/author/${authors[0].slug}`
            }"
          },`
              : ''
          }
          "publisher": {
            "@type": "Organization",
            "name": "Which?",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.which.co.uk/favicon-512.png",
              "width": 512,
              "height": 512
            }
          },
          ${video ? getVideoSchema(video) : ''}
          "datePublished": "${getTimestampToIso(publishedDateTime)}",
          "dateModified": "${getTimestampToIso(updatedDateTime)}"
        }
      `}</script>
  )

  schemas.push(baseSchema)

  if (reviewSchema) {
    const generatedReviewSchema = generateSchemaFromSnippet(reviewSchema)

    return schemas.concat(generatedReviewSchema)
  }

  return schemas
}

const getVideoSchema = (videoData: VideoData) => {
  const heroVideoSeo = videoData.heroVideo?.seo || null
  const embeddedVideosSeo = videoData.customSeo || []
  const allVideosSeoData: VideoSeo[] = [heroVideoSeo, ...(embeddedVideosSeo ?? [])].filter(
    (video): video is VideoSeo => video !== null
  )
  const videosCount = allVideosSeoData.length

  if (!videosCount) {
    return ''
  }

  // Single video
  if (videosCount === 1) {
    const [videoSeo] = allVideosSeoData
    const singleVideoSchema = getSingleVideoSchema(videoSeo)

    return `"video": ${singleVideoSchema}`
  }

  // Multi video
  const videosSchema = allVideosSeoData.reduce((schema, videoSeo) => {
    const videoSchema = getSingleVideoSchema(videoSeo)

    return `${schema} ${videoSchema}`
  }, '')

  return `"video": [${videosSchema.slice(0, videosSchema.length - 1)}],`
}

const getSingleVideoSchema = (videoSeo: VideoSeo) => `{
  "@type": "VideoObject",
  "@context": "https://schema.org",
  "name": "${videoSeo.name}",
  "description": "${videoSeo.description}",
  "thumbnailUrl": "${videoSeo.thumbnailUrl}",
  "uploadDate": "${videoSeo.uploadDate}",
  "dateModified": "${videoSeo.dateModified}",
  "dateCreated": "${videoSeo.dateCreated}",
  "embedUrl": "${videoSeo.embedUrl}",
  "contentUrl": "${videoSeo.contentUrl}",
  "duration": "${videoSeo.duration}"
},`
